import { useMemo, useState } from "react";
import { useCustomMutation, useGetIdentity, useList, useTranslate } from "@refinedev/core";
import type { HttpError } from "@refinedev/core";
import {
  Button,
  DataTable,
  Field,
  Modal,
  PasswordField,
  StatusBadge,
  ToggleField,
  type Column,
} from "@fawkes/ui";

import { ErrorState, LoadingState } from "../../components/states";
import type { AdminUser } from "./types";
import { Detail } from "./DetailRow";

// jmix-9.1 / jmix-parity (user-management): Users — list + detail PLUS the full CRUD + password lifecycle
// the legacy UserListView / UserDetailView provided (create / edit / change-password / reissue reset link
// / soft-delete). GET /api/admin/users returns the full AdminUserDto per row, so the modals reuse the row
// (no second fetch). Foster users are provisioned by magic-login and service accounts by 9.2; only the
// interactive HUMAN-account lifecycle lives here — edit / delete / password actions are gated to HUMAN
// rows (the server 404s a non-HUMAN target). There is no general role editor: the one role managed here
// is the admin role (admin-role-grant) — a create-dialog toggle plus grant/revoke in the edit dialog. The
// server refuses (409) anything that would leave no active admin; the dataProvider only carries the status
// code, so a 409 is told apart by context (deleting yourself vs. the last-admin guard).
//
// parity MED-3: two legacy UserDetailView/UserListView copy strings are deliberately NOT ported.
// (1) `noAssignedRolesNotification` ("assign roles to the new user") was a post-create nudge toward the
//     Jmix role editor; the admin toggle in the create dialog replaces it. (2) the `additionalMenu` ("Meer"/"Additional")
//     dropdown grouped that role action + other secondary items; with the dropdown's contents removed,
//     the remaining per-row actions are inline (View/Edit) and the detail modal is sectioned, so there
//     is no "More" grouping to label. Both are dropped cleanly — no dead control resurrected.

function userTypeKey(userType: string): string {
  const t = userType.toLowerCase();
  return t === "account" || t === "person" || t === "service"
    ? `admin.users.type.${t}`
    : "admin.users.type.other";
}

function isAccount(u: AdminUser): boolean {
  return u.userType.toLowerCase() === "account";
}

function statusOf(error: unknown): number | undefined {
  return (error as HttpError | undefined)?.statusCode;
}

// Mirrors the server policy (CreateUserRequest / PasswordResetTokenService.MIN_PASSWORD_LENGTH); the
// client enforces it so a legitimate operator never trips the server's 400.
const MIN_PASSWORD_LENGTH = 8;

type CreateForm = {
  username: string;
  password: string;
  passwordConfirm: string;
  email: string;
  firstName: string;
  lastName: string;
  timeZoneId: string;
  active: boolean;
  admin: boolean;
};

const EMPTY_CREATE: CreateForm = {
  username: "",
  password: "",
  passwordConfirm: "",
  email: "",
  firstName: "",
  lastName: "",
  timeZoneId: "",
  active: true,
  admin: false,
};

type EditForm = {
  firstName: string;
  lastName: string;
  email: string;
  timeZoneId: string;
  active: boolean;
};

export function Users() {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useList<AdminUser>({
    resource: "admin/users",
    pagination: { mode: "off" },
  });
  const { mutate, isLoading: mutating } = useCustomMutation();
  // The SPA identity id is the token `sub` = the operator's username.
  const { data: identity } = useGetIdentity<{ id?: string }>();
  const isSelf = (u: AdminUser) => identity?.id !== undefined && identity.id === u.username;

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AdminUser | null>(null);

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const [createError, setCreateError] = useState<string | undefined>();

  // Edit
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editError, setEditError] = useState<string | undefined>();

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>();

  // Change password
  const [passwordTarget, setPasswordTarget] = useState<AdminUser | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [passwordError, setPasswordError] = useState<string | undefined>();

  // Reissue reset link (reveal-once)
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetLinkCopied, setResetLinkCopied] = useState(false);
  const [resetError, setResetError] = useState<string | undefined>();

  // Admin role grant / revoke (confirm first)
  const [roleTarget, setRoleTarget] = useState<{ user: AdminUser; grant: boolean } | null>(null);
  const [roleError, setRoleError] = useState<string | undefined>();

  const rows = data?.data ?? [];

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...rows].sort((a, b) => a.username.localeCompare(b.username));
    if (q.length === 0) {
      return sorted;
    }
    return sorted.filter(
      (u) => u.username.toLowerCase().includes(q) || (u.email ?? "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  function openCreate() {
    setCreateForm(EMPTY_CREATE);
    setCreateError(undefined);
    setCreateOpen(true);
  }

  function submitCreate() {
    setCreateError(undefined);
    if (createForm.username.trim().length === 0) {
      setCreateError(t("admin.users.create.error.usernameRequired"));
      return;
    }
    if (createForm.password.length < MIN_PASSWORD_LENGTH) {
      setCreateError(t("admin.users.create.error.passwordWeak"));
      return;
    }
    if (createForm.password !== createForm.passwordConfirm) {
      setCreateError(t("admin.users.password.error.mismatch"));
      return;
    }
    mutate(
      {
        url: "admin/users",
        method: "post",
        values: {
          username: createForm.username.trim(),
          password: createForm.password,
          active: createForm.active,
          email: createForm.email.trim() || null,
          firstName: createForm.firstName.trim() || null,
          lastName: createForm.lastName.trim() || null,
          timeZoneId: createForm.timeZoneId.trim() || null,
          admin: createForm.admin,
        },
      },
      {
        onSuccess: () => {
          setCreateOpen(false);
          void refetch();
        },
        onError: (error) => {
          const status = statusOf(error);
          setCreateError(
            status === 409
              ? t("admin.users.create.error.duplicate")
              : status === 400
                ? t("admin.users.create.error.invalid")
                : t("admin.users.create.error.generic"),
          );
        },
      },
    );
  }

  function openEdit(u: AdminUser) {
    setEditTarget(u);
    setEditForm({
      firstName: u.firstName ?? "",
      lastName: u.lastName ?? "",
      email: u.email ?? "",
      timeZoneId: u.timeZoneId ?? "",
      active: u.active,
    });
    setEditError(undefined);
  }

  function submitEdit() {
    if (!editTarget || !editForm) {
      return;
    }
    setEditError(undefined);
    mutate(
      {
        url: `admin/users/${editTarget.id}`,
        method: "put",
        values: {
          firstName: editForm.firstName.trim() || null,
          lastName: editForm.lastName.trim() || null,
          email: editForm.email.trim() || null,
          timeZoneId: editForm.timeZoneId.trim() || null,
          active: editForm.active,
        },
      },
      {
        onSuccess: () => {
          setEditTarget(null);
          setEditForm(null);
          void refetch();
        },
        onError: (error) => {
          const status = statusOf(error);
          setEditError(
            status === 404
              ? t("admin.users.edit.error.notFound")
              : status === 400
                ? t("admin.users.create.error.invalid")
                : status === 409
                  ? t("admin.users.error.lastAdmin")
                  : t("admin.users.edit.error.generic"),
          );
        },
      },
    );
  }

  function confirmDelete() {
    if (!deleteTarget) {
      return;
    }
    setDeleteError(undefined);
    mutate(
      { url: `admin/users/${deleteTarget.id}`, method: "delete", values: {} },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          void refetch();
        },
        onError: (error) => {
          const status = statusOf(error);
          setDeleteError(
            status !== 409
              ? t("admin.users.delete.error.generic")
              : isSelf(deleteTarget)
                ? t("admin.users.delete.error.self")
                : t("admin.users.error.lastAdmin"),
          );
        },
      },
    );
  }

  function openRole(u: AdminUser, grant: boolean) {
    setRoleTarget({ user: u, grant });
    setRoleError(undefined);
  }

  // admin-role-grant: PUT grants, DELETE revokes; both idempotent. The change is immediate server-side
  // (authorities are re-read per request), so revoking your own role ends your admin access at once.
  function confirmRole() {
    if (!roleTarget) {
      return;
    }
    const { user, grant } = roleTarget;
    setRoleError(undefined);
    mutate(
      { url: `admin/users/${user.id}/admin-role`, method: grant ? "put" : "delete", values: {} },
      {
        onSuccess: () => {
          setRoleTarget(null);
          setEditTarget((current) => (current && current.id === user.id ? { ...current, admin: grant } : current));
          void refetch();
        },
        onError: (error) => {
          const status = statusOf(error);
          setRoleError(
            status === 409
              ? t("admin.users.error.lastAdmin")
              : status === 404
                ? t("admin.users.edit.error.notFound")
                : t("admin.users.role.error.generic"),
          );
        },
      },
    );
  }

  function openPassword(u: AdminUser) {
    setPasswordTarget(u);
    setNewPassword("");
    setNewPasswordConfirm("");
    setPasswordError(undefined);
  }

  function submitPassword() {
    if (!passwordTarget) {
      return;
    }
    setPasswordError(undefined);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(t("admin.users.create.error.passwordWeak"));
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordError(t("admin.users.password.error.mismatch"));
      return;
    }
    mutate(
      {
        url: `admin/users/${passwordTarget.id}/change-password`,
        method: "post",
        values: { newPassword },
      },
      {
        onSuccess: () => {
          setPasswordTarget(null);
        },
        onError: (error) => {
          const status = statusOf(error);
          setPasswordError(
            status === 404
              ? t("admin.users.edit.error.notFound")
              : status === 400
                ? t("admin.users.create.error.passwordWeak")
                : t("admin.users.password.error.generic"),
          );
        },
      },
    );
  }

  // jmix-parity: reissue the single-use password-reset link (legacy UserDetailView action). The server
  // returns the token plaintext exactly once; we compose the /set-password#<token> link the operator
  // consumes. Held in state only until copied — reissuing also invalidates any prior outstanding link.
  function openReset(u: AdminUser) {
    setResetTarget(u);
    setResetLink(null);
    setResetLinkCopied(false);
    setResetError(undefined);
    mutate(
      { url: `admin/users/${u.id}/reset-link`, method: "post", values: {} },
      {
        onSuccess: (res) => {
          const token = (res?.data as { token?: string } | undefined)?.token;
          if (token) {
            setResetLink(`${window.location.origin}/set-password#${token}`);
          } else {
            setResetError(t("admin.users.resetLink.error"));
          }
        },
        onError: () => setResetError(t("admin.users.resetLink.error")),
      },
    );
  }

  async function copyResetLink() {
    if (!resetLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(resetLink);
    } catch {
      setResetError(t("admin.users.resetLink.error"));
      return;
    }
    // Do not leave the secret sitting in component state after copy.
    setResetLink(null);
    setResetLinkCopied(true);
  }

  const activeBadge = (active: boolean) => (
    <StatusBadge tone={active ? "success" : "neutral"}>
      {t(active ? "admin.common.active" : "admin.common.inactive")}
    </StatusBadge>
  );

  const roleBadge = (u: AdminUser) =>
    u.admin ? <StatusBadge tone="warning">{t("admin.users.role.admin")}</StatusBadge> : "—";

  const columns: Column<AdminUser>[] = [
    { header: t("admin.users.column.username"), cell: (u) => u.username },
    { header: t("admin.users.column.firstName"), cell: (u) => u.firstName || "—" },
    { header: t("admin.users.column.lastName"), cell: (u) => u.lastName || "—" },
    { header: t("admin.users.column.email"), cell: (u) => u.email || "—" },
    { header: t("admin.users.column.timeZone"), cell: (u) => u.timeZoneId || "—" },
    {
      header: t("admin.users.column.type"),
      cell: (u) => <StatusBadge tone="neutral">{t(userTypeKey(u.userType))}</StatusBadge>,
    },
    { header: t("admin.users.column.active"), cell: (u) => activeBadge(u.active) },
    { header: t("admin.users.column.role"), cell: (u) => roleBadge(u) },
    {
      header: t("admin.common.actions"),
      align: "right",
      cell: (u) => (
        <span className="fawkes-admin__row-actions">
          <Button variant="secondary" onClick={() => setSelected(u)}>
            {t("admin.common.view")}
          </Button>
          {isAccount(u) ? (
            <Button variant="secondary" onClick={() => openEdit(u)}>
              {t("admin.common.edit")}
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <div className="fawkes-admin-page">
      <div className="fawkes-admin__toolbar">
        <Field
          label={t("admin.users.search.label")}
          placeholder={t("admin.users.search.placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button onClick={openCreate}>{t("admin.users.create.open")}</Button>
      </div>

      <DataTable
        columns={columns}
        rows={filteredRows}
        getRowKey={(u) => u.id}
        empty={<span className="fawkes-portal__muted">{t("admin.users.empty")}</span>}
      />

      {/* Create */}
      <Modal
        open={createOpen}
        title={t("admin.users.create.title")}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t("admin.common.cancel")}
            </Button>
            <Button onClick={submitCreate} disabled={mutating}>
              {mutating ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </>
        }
      >
        <p className="fawkes-portal__muted">{t("admin.users.create.help")}</p>
        <Field
          label={t("admin.users.column.username")}
          value={createForm.username}
          onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
        />
        <PasswordField
          label={t("admin.users.create.password")}
          revealLabel={t("auth.field.revealPassword")}
          hideLabel={t("auth.field.hidePassword")}
          value={createForm.password}
          onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
        />
        <PasswordField
          label={t("admin.users.password.confirm")}
          revealLabel={t("auth.field.revealPassword")}
          hideLabel={t("auth.field.hidePassword")}
          value={createForm.passwordConfirm}
          onChange={(e) => setCreateForm((f) => ({ ...f, passwordConfirm: e.target.value }))}
        />
        <Field
          label={t("admin.users.column.firstName")}
          value={createForm.firstName}
          onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))}
        />
        <Field
          label={t("admin.users.column.lastName")}
          value={createForm.lastName}
          onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))}
        />
        <Field
          label={t("admin.users.column.email")}
          type="email"
          value={createForm.email}
          onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
        />
        <Field
          label={t("admin.users.create.timeZone")}
          placeholder="Europe/Amsterdam"
          value={createForm.timeZoneId}
          onChange={(e) => setCreateForm((f) => ({ ...f, timeZoneId: e.target.value }))}
        />
        <ToggleField
          label={t("admin.users.column.active")}
          checked={createForm.active}
          onChange={(e) => setCreateForm((f) => ({ ...f, active: e.target.checked }))}
        />
        <ToggleField
          label={t("admin.users.create.admin")}
          checked={createForm.admin}
          onChange={(e) => setCreateForm((f) => ({ ...f, admin: e.target.checked }))}
        />
        {createError ? (
          <p className="fawkes-portal__error" role="alert">
            {createError}
          </p>
        ) : null}
      </Modal>

      {/* Edit */}
      <Modal
        open={editTarget !== null && editForm !== null}
        title={editTarget ? t("admin.users.edit.title") : ""}
        onClose={() => {
          setEditTarget(null);
          setEditForm(null);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setEditTarget(null);
                setEditForm(null);
              }}
            >
              {t("admin.common.cancel")}
            </Button>
            <Button onClick={submitEdit} disabled={mutating}>
              {mutating ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </>
        }
      >
        {editTarget && editForm ? (
          <>
            <p className="fawkes-portal__muted">{editTarget.username}</p>
            <Field
              label={t("admin.users.column.firstName")}
              value={editForm.firstName}
              onChange={(e) => setEditForm((f) => (f ? { ...f, firstName: e.target.value } : f))}
            />
            <Field
              label={t("admin.users.column.lastName")}
              value={editForm.lastName}
              onChange={(e) => setEditForm((f) => (f ? { ...f, lastName: e.target.value } : f))}
            />
            <Field
              label={t("admin.users.column.email")}
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((f) => (f ? { ...f, email: e.target.value } : f))}
            />
            <Field
              label={t("admin.users.create.timeZone")}
              placeholder="Europe/Amsterdam"
              value={editForm.timeZoneId}
              onChange={(e) => setEditForm((f) => (f ? { ...f, timeZoneId: e.target.value } : f))}
            />
            <ToggleField
              label={t("admin.users.column.active")}
              checked={editForm.active}
              onChange={(e) => setEditForm((f) => (f ? { ...f, active: e.target.checked } : f))}
            />

            <h3 className="fawkes-admin__section">{t("admin.users.role.section")}</h3>
            <p className="fawkes-portal__muted">
              {t(editTarget.admin ? "admin.users.role.isAdmin" : "admin.users.role.notAdmin")}
            </p>
            <Button
              variant="secondary"
              disabled={mutating}
              onClick={() => openRole(editTarget, !editTarget.admin)}
            >
              {t(editTarget.admin ? "admin.users.role.revoke" : "admin.users.role.grant")}
            </Button>

            <h3 className="fawkes-admin__section">{t("admin.users.password.section")}</h3>
            <div className="fawkes-admin__row-actions">
              <Button variant="secondary" disabled={mutating} onClick={() => openPassword(editTarget)}>
                {t("admin.users.password.open")}
              </Button>
              <Button variant="secondary" disabled={mutating} onClick={() => openReset(editTarget)}>
                {t("admin.users.resetLink.button")}
              </Button>
            </div>

            <h3 className="fawkes-admin__section">{t("admin.users.delete.section")}</h3>
            <Button variant="secondary" disabled={mutating} onClick={() => setDeleteTarget(editTarget)}>
              {t("admin.users.delete.open")}
            </Button>

            {editError ? (
              <p className="fawkes-portal__error" role="alert">
                {editError}
              </p>
            ) : null}
          </>
        ) : null}
      </Modal>

      {/* Change password */}
      <Modal
        open={passwordTarget !== null}
        title={t("admin.users.password.title")}
        onClose={() => setPasswordTarget(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPasswordTarget(null)}>
              {t("admin.common.cancel")}
            </Button>
            <Button onClick={submitPassword} disabled={mutating}>
              {mutating ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </>
        }
      >
        {passwordTarget ? (
          <>
            <p className="fawkes-portal__muted">{passwordTarget.username}</p>
            <PasswordField
              label={t("admin.users.password.new")}
              revealLabel={t("auth.field.revealPassword")}
              hideLabel={t("auth.field.hidePassword")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <PasswordField
              label={t("admin.users.password.confirm")}
              revealLabel={t("auth.field.revealPassword")}
              hideLabel={t("auth.field.hidePassword")}
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
            />
            {passwordError ? (
              <p className="fawkes-portal__error" role="alert">
                {passwordError}
              </p>
            ) : null}
          </>
        ) : null}
      </Modal>

      {/* Reissue reset link (reveal-once) */}
      <Modal
        open={resetTarget !== null}
        title={t("admin.users.resetLink.title")}
        onClose={() => setResetTarget(null)}
        footer={
          <Button variant="secondary" onClick={() => setResetTarget(null)}>
            {t("admin.common.close")}
          </Button>
        }
      >
        {resetTarget ? (
          <div className="fawkes-admin__reset-link">
            <p className="fawkes-portal__muted">{resetTarget.username}</p>
            {resetLink ? (
              <div className="fawkes-admin__secret" role="status">
                <strong>{t("admin.users.resetLink.revealTitle")}</strong>
                <p className="fawkes-portal__muted">{t("admin.users.resetLink.revealOnce")}</p>
                <code className="fawkes-admin__secret-value">{resetLink}</code>
                <Button variant="secondary" onClick={() => void copyResetLink()}>
                  {t("admin.users.resetLink.copy")}
                </Button>
              </div>
            ) : null}
            {resetLinkCopied ? (
              <p className="fawkes-portal__muted" role="status">
                {t("admin.users.resetLink.copied")}
              </p>
            ) : null}
            {resetError ? (
              <p className="fawkes-portal__error" role="alert">
                {resetError}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>

      {/* Admin role grant / revoke confirm */}
      <Modal
        open={roleTarget !== null}
        title={roleTarget?.grant ? t("admin.users.role.grantTitle") : t("admin.users.role.revokeTitle")}
        onClose={() => setRoleTarget(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRoleTarget(null)}>
              {t("admin.common.cancel")}
            </Button>
            <Button onClick={confirmRole} disabled={mutating}>
              {roleTarget?.grant ? t("admin.users.role.confirmGrant") : t("admin.users.role.confirmRevoke")}
            </Button>
          </>
        }
      >
        {roleTarget ? (
          <>
            <p className="fawkes-portal__banner" role="alert">
              {t(roleTarget.grant ? "admin.users.role.grantWarning" : "admin.users.role.revokeWarning")}
            </p>
            {!roleTarget.grant && isSelf(roleTarget.user) ? (
              <p className="fawkes-portal__banner" role="alert">
                {t("admin.users.role.revokeSelfWarning")}
              </p>
            ) : null}
            <p className="fawkes-portal__muted">{roleTarget.user.username}</p>
            {roleError ? (
              <p className="fawkes-portal__error" role="alert">
                {roleError}
              </p>
            ) : null}
          </>
        ) : null}
      </Modal>

      {/* Delete confirm (danger) */}
      <Modal
        open={deleteTarget !== null}
        title={t("admin.users.delete.title")}
        onClose={() => setDeleteTarget(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              {t("admin.common.cancel")}
            </Button>
            <Button onClick={confirmDelete} disabled={mutating}>
              {t("admin.users.delete.confirm")}
            </Button>
          </>
        }
      >
        {deleteTarget ? (
          <>
            <p className="fawkes-portal__banner" role="alert">
              {t("admin.users.delete.warning")}
            </p>
            <p className="fawkes-portal__muted">{deleteTarget.username}</p>
            {deleteError ? (
              <p className="fawkes-portal__error" role="alert">
                {deleteError}
              </p>
            ) : null}
          </>
        ) : null}
      </Modal>

      {/* Detail */}
      <Modal
        open={selected !== null}
        title={selected?.username ?? ""}
        onClose={() => setSelected(null)}
        footer={
          <Button variant="secondary" onClick={() => setSelected(null)}>
            {t("admin.common.close")}
          </Button>
        }
      >
        {selected ? (
          <dl className="fawkes-admin__fields">
            <Detail label={t("admin.users.column.username")} value={selected.username} />
            <Detail label={t("admin.users.column.firstName")} value={selected.firstName} />
            <Detail label={t("admin.users.column.lastName")} value={selected.lastName} />
            <Detail label={t("admin.users.column.email")} value={selected.email} />
            <Detail label={t("admin.users.column.timeZone")} value={selected.timeZoneId} />
            <Detail label={t("admin.users.column.type")} value={t(userTypeKey(selected.userType))} />
            <div className="fawkes-admin__field">
              <dt>{t("admin.users.column.active")}</dt>
              <dd>{activeBadge(selected.active)}</dd>
            </div>
            <div className="fawkes-admin__field">
              <dt>{t("admin.users.column.role")}</dt>
              <dd>{roleBadge(selected)}</dd>
            </div>
          </dl>
        ) : null}
      </Modal>
    </div>
  );
}
